import { Fragment, useState, memo, useCallback, useMemo } from 'react';
import { sceneGroups, sceneNames, demoScenes } from '@/mocks/demo';
import {
  productArrangements,
  PATTERN_TEMPLATE_PREFIX,
  isPatternTemplate,
} from '@/mocks/app-data';
import StepHeader from './StepHeader';

/** Tag-overlap score: how many tags in `b` also appear in `a`. Simple
 *  count works fine at our catalog size (~22 scenes, ~6 tags each).
 *  Both arrays are lowercase by convention; we don't normalize at call
 *  time to keep this hot path cheap. */
function tagOverlapScore(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const setA = new Set(a);
  let n = 0;
  for (const t of b) if (setA.has(t)) n += 1;
  return n;
}

/** Bonus weight for productCategory matching a tag exactly. Category is
 *  a single high-signal label (e.g. "skincare") and matters more than
 *  any individual vibe tag. */
const CATEGORY_BONUS = 2;
/** Minimum score for a scene to surface in "Recommended for you". Lower
 *  and the group fills with noise; higher and most products get an empty
 *  recommended list. 1 = at least one tag in common (or the category
 *  matches a scene tag, which is worth CATEGORY_BONUS alone). */
const MIN_REC_SCORE = 1;
/** Cap on how many scenes show in the recommended group. Above this the
 *  user is better off browsing by aesthetic. */
const MAX_RECOMMENDATIONS = 6;

// 10 per page = ~2 full rows on lg (5×2) / 2.5 on xl. Bumped from 4 when
// the grid widened to match the Fashion Model picker's breakpoints — at
// 4-per-page the wider grid left ugly half-empty rows.
const PER_PAGE = 10;

interface SceneCardProps {
  scene: {
    id: string;
    name: string;
    description: string;
    image: string;
  };
  isActive: boolean;
  onSelect: (id: string) => void;
}

// Memoized card — only re-renders when its own props change.
// Card is always locked to a 1:1 aspect (aspect-square on the button itself,
// not the inner image) so the full template thumbnail is always visible
// regardless of viewport height. Card width is bounded by its grid cell.
const SceneCard = memo(function SceneCard({ scene, isActive, onSelect }: SceneCardProps) {
  const handleClick = useCallback(() => {
    onSelect(scene.id);
  }, [scene.id, onSelect]);

  return (
    <button
      onClick={handleClick}
      className={`group relative rounded-xl overflow-hidden border-2 text-left cursor-pointer aspect-square w-full active:scale-[0.98] ${
        isActive
          ? 'border-accent shadow-lg shadow-accent/15'
          : 'border-transparent hover:border-surface-dark/50 bg-surface hover:shadow-sm'
      }`}
    >
      {/* Image fills the 1:1 button. object-contain so the full image is
          always visible — never cropped. */}
      <img
        src={scene.image}
        alt={scene.name}
        className="absolute inset-0 w-full h-full object-contain"
        loading="eager"
      />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-dark/70 to-transparent pointer-events-none" />

      {/* Name + desc overlay */}
      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-xs font-bold text-white leading-tight">{scene.name}</p>
        <p className="text-xs text-white/70 leading-tight">{scene.description}</p>
      </div>

      {/* Active check */}
      {isActive && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}
    </button>
  );
});

interface StepBackgroundProps {
  selected: string | null;
  onSelect: (id: string) => void;
  /** Product category from /api/analyze-product (e.g. "skincare").
   *  Optional — when present, drives the "Recommended for you" tab. */
  productCategory?: string;
  /** Vibe tags from /api/analyze-product (3–6 strings). Combined with
   *  productCategory to rank scenes by tag overlap. Optional. */
  productTags?: string[];
  /** When true, skip the StepHeader. /appv2's Section wrapper supplies
   *  its own header so the inner one would be a duplicate. */
  compact?: boolean;
  /** When true, show a "Scene / Pattern" tab toggle at the top. Pattern
   *  tab swaps the scene grid for a 6-card grid of art-directed
   *  arrangements (each picking a synthetic `pattern-<id>` template
   *  id). Currently Product Shot route only — Carousel / Social /
   *  Fashion stays on the default scene-only view. */
  showPatternTab?: boolean;
}

// Pseudo-group id for the runtime-computed Recommended tab. Not in
// sceneGroups (those are static) — we splice it in dynamically and
// dispatch off this id when rendering the active tab.
const RECOMMENDED_GROUP_ID = '__recommended';

export default function StepBackground({
  selected,
  onSelect,
  showPatternTab = false,
  productCategory,
  productTags,
  compact,
}: StepBackgroundProps) {
  // Local tab state for the Scene / Pattern toggle. Seeded from
  // `selected` so a snapshot-restored "pattern-*" id opens the Pattern
  // tab on remount. Managed locally after — switching tabs is decoupled
  // from selection so the user can browse a tab without committing.
  const [mode, setMode] = useState<'scene' | 'pattern'>(
    isPatternTemplate(selected) ? 'pattern' : 'scene',
  );

  // Compute recommendations once per render of the relevant inputs. Cheap
  // — 22 scenes × ~6 tags each is well under a millisecond.
  const recommendedScenes = useMemo(() => {
    const cat = productCategory?.trim().toLowerCase();
    const tags = (productTags ?? []).map((t) => t.toLowerCase());
    if (!cat && tags.length === 0) return [];
    return demoScenes
      .map((scene) => {
        let score = tagOverlapScore(scene.tags, tags);
        if (cat && scene.tags.includes(cat)) score += CATEGORY_BONUS;
        return { scene, score };
      })
      .filter((r) => r.score >= MIN_REC_SCORE)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_RECOMMENDATIONS)
      .map((r) => r.scene);
  }, [productCategory, productTags]);

  const hasRecommendations = recommendedScenes.length > 0;

  // Default landing tab — Recommended if we have scores, otherwise the
  // first aesthetic group. Recommended is ALWAYS rendered as a tab now
  // (even when empty — shows an "upload to populate" empty state on the
  // panel below), but we only default-select it when there are actual
  // results so the user doesn't land on a blank pane.
  const [activeGroup, setActiveGroup] = useState<string>(() =>
    hasRecommendations ? RECOMMENDED_GROUP_ID : (sceneGroups[0]?.id ?? ''),
  );
  const [page, setPage] = useState(0);

  // Build the displayed tab list. The Recommended tab is always at index
  // 0 (stable visual position regardless of whether insights are loaded);
  // aesthetic groups follow in static order. The render path injects a
  // vertical separator between Recommended and the first aesthetic tab.
  const displayGroups = useMemo(() => {
    const groups = sceneGroups.map((g) => ({
      id: g.id,
      name: g.name,
      description: g.description,
      icon: g.icon,
      scenes: g.scenes,
    }));
    groups.unshift({
      id: RECOMMENDED_GROUP_ID,
      name: 'Recommended for you',
      description: hasRecommendations
        ? `Picked for your ${productCategory || 'product'} based on what you uploaded`
        : 'Upload a product on Step 3 to see personalized picks here',
      icon: 'ri-sparkling-2-line',
      scenes: recommendedScenes,
    });
    return groups;
  }, [hasRecommendations, recommendedScenes, productCategory]);

  const currentGroup = displayGroups.find((g) => g.id === activeGroup) ?? displayGroups[0];
  const currentScenes = currentGroup?.scenes ?? [];
  const totalPages = Math.ceil(currentScenes.length / PER_PAGE);
  const currentPageScenes = currentScenes.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const handleGroupChange = useCallback((groupId: string) => {
    setActiveGroup(groupId);
    setPage(0);
  }, []);

  return (
    // In /app: h-full + flex column so the grid below can flex-1 + scroll
    // inside the fixed-viewport wizard panel.
    // In /appv2 (compact): no height constraint — content flows in the
    // page's natural scroll, the inner overflow-y-auto is dropped below.
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {/* Header */}
      {!compact && (
        <StepHeader
          icon="ri-palette-line"
          title="Choose a Background"
          subtitle="Pick a lifestyle scene OR a styled pattern arrangement. Scene = product placed in a real environment. Pattern = product art-directed on a clean colored backdrop."
        />
      )}

      {/* Scene / Pattern tab toggle — Product Shot route only.
          The active mode is local state, NOT derived from `selected`
          (otherwise switching tabs is a chicken-and-egg loop:
          clearing selected reverts the tab back to Scene). Mode is
          seeded from `selected` on first render and managed locally
          thereafter. Clicking a tab also clears `selected` so the
          wizard can't proceed without a fresh pick from the new tab. */}
      {showPatternTab && (
        <div className="flex-shrink-0 mb-4 flex items-center gap-1 p-1 bg-surface-dark/20 rounded-full self-start">
          <button
            type="button"
            onClick={() => {
              if (mode !== 'scene') {
                setMode('scene');
                onSelect('');
              }
            }}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer ${
              mode === 'scene'
                ? 'bg-white text-dark shadow-sm'
                : 'text-dark-muted hover:text-dark'
            }`}
          >
            <i className="ri-image-line text-base" />
            Scene
          </button>
          <button
            type="button"
            onClick={() => {
              if (mode !== 'pattern') {
                setMode('pattern');
                onSelect('');
              }
            }}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer ${
              mode === 'pattern'
                ? 'bg-white text-dark shadow-sm'
                : 'text-dark-muted hover:text-dark'
            }`}
          >
            <i className="ri-shape-line text-base" />
            Pattern
          </button>
        </div>
      )}

      {/* Pattern view — replaces the scene grid when the user is on the
          Pattern tab. 6 cards in a responsive grid; each shows the
          backdrop color as the card's background tint plus the icon
          and label. Selecting a card sets `selected` to
          `pattern-<arrangement_id>` which the backend dispatches on. */}
      {showPatternTab && mode === 'pattern' ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-3">
            {productArrangements.map((opt) => {
              const templateId = `${PATTERN_TEMPLATE_PREFIX}${opt.id}`;
              const isActive = selected === templateId;
              // Determine readable text color by simple luminance on the
              // backdrop hex — dark backdrops get white text, light get dark.
              const hex = opt.backdropColor.replace('#', '');
              const r = parseInt(hex.slice(0, 2), 16);
              const g = parseInt(hex.slice(2, 4), 16);
              const b = parseInt(hex.slice(4, 6), 16);
              const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
              const isDark = lum < 0.5;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onSelect(templateId)}
                  title={opt.description}
                  className={`relative aspect-[4/3] rounded-2xl border-2 transition-all cursor-pointer overflow-hidden text-left ${
                    isActive
                      ? 'border-primary shadow-lg shadow-primary/15'
                      : 'border-surface-dark/30 hover:border-primary/40 hover:shadow-md'
                  }`}
                  style={{ backgroundColor: opt.backdropColor }}
                >
                  {isActive && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center z-10">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                    <i
                      className={`${opt.icon} text-3xl mb-2`}
                      style={{ color: isDark ? '#ffffff' : '#2A2A2A' }}
                    />
                    <p
                      className="font-display font-bold text-sm text-center"
                      style={{ color: isDark ? '#ffffff' : '#2A2A2A' }}
                    >
                      {opt.label}
                    </p>
                    <p
                      className="text-xs text-center mt-1 leading-snug px-2"
                      style={{ color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(42,42,42,0.65)' }}
                    >
                      {opt.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
      <>
      {/* Theme pill tabs — Recommended at index 0 (always rendered),
          then a vertical separator, then the 5 aesthetic groups. The
          Recommended pill uses an accent treatment so it reads as a
          different category of tab, not just another aesthetic. */}
      <div className="flex-shrink-0 relative mb-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 px-1" style={{ scrollbarWidth: 'none' }}>
          {displayGroups.map((group, idx) => {
            const isActive = activeGroup === group.id;
            const isRecommended = group.id === RECOMMENDED_GROUP_ID;
            // Insert a thin vertical divider between Recommended and the
            // first aesthetic tab so they read as distinct sections.
            const showSeparator = idx === 1 && displayGroups[0]?.id === RECOMMENDED_GROUP_ID;
            // Active treatment: accent color for Recommended, primary for
            // aesthetic tabs. Keeps the Recommended pill visually distinct
            // even when selected.
            const activeClass = isRecommended
              ? 'bg-accent text-white shadow-md shadow-accent/20'
              : 'bg-primary text-white shadow-md shadow-primary/20';
            // Idle treatment: Recommended also gets the accent tint when
            // inactive so a user scanning the row notices it.
            const idleClass = isRecommended
              ? 'bg-accent/10 border border-accent/40 text-accent hover:bg-accent/15'
              : 'bg-white border border-surface-dark/30 text-dark-muted hover:border-primary/30 hover:text-dark';
            return (
              <Fragment key={group.id}>
                {showSeparator && (
                  <div className="flex-shrink-0 w-px h-6 bg-surface-dark/40 mx-1" aria-hidden="true" />
                )}
                <button
                  onClick={() => handleGroupChange(group.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    isActive ? activeClass : idleClass
                  }`}
                >
                  <i className={`${group.icon} ${isActive ? 'text-white' : isRecommended ? 'text-accent' : 'text-primary'}`} />
                  <span>{group.name}</span>
                  <span className={`text-xs px-1 py-0.5 rounded-full ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : isRecommended
                        ? 'bg-accent/20 text-accent'
                        : 'bg-surface text-dark-muted'
                  }`}>
                    {group.scenes.length}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* Pagination + theme info */}
      <div className="flex-shrink-0 flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs text-dark-muted">
          <i className={`${currentGroup?.icon || 'ri-folder-line'} text-primary text-sm`} />
          <span className="font-medium text-dark">{currentGroup?.name}</span>
          <span className="hidden sm:inline">{currentGroup?.description}</span>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label="Previous page"
              className="w-10 h-10 rounded-lg border-2 border-surface-dark bg-white flex items-center justify-center text-dark hover:text-white hover:bg-primary hover:border-primary transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <div className="flex items-center gap-1.5 px-1">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    i === page ? 'bg-primary w-5' : 'bg-surface-dark w-2 hover:bg-primary/40'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              aria-label="Next page"
              className="w-10 h-10 rounded-lg border-2 border-surface-dark bg-white flex items-center justify-center text-dark hover:text-white hover:bg-primary hover:border-primary transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        )}
      </div>

      {/* Card grid — matches the Fashion Model picker (StepFashionModel)
          breakpoints so thumbnails are the same physical size across both
          routes' Step 4. 2-up on mobile, scaling to 5-up on wide monitors.
          Cards are locked 1:1 (backgrounds aren't portraits like the
          fashion cards, but the GRID sizing is identical). overflow-y-auto
          handles cases where PER_PAGE × card-height exceeds the viewport
          — but only in /app's fixed-viewport panel. In /appv2 (compact)
          we let the page scroll instead. */}
      <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        {/* Empty-state for the Recommended tab when analyze-product hasn't
            populated insights yet (or scored 0 matches). Renders in place
            of the grid so the user sees explicit guidance, not a blank
            pane. */}
        {activeGroup === RECOMMENDED_GROUP_ID && currentPageScenes.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12">
            <div className="w-14 h-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <i className="ri-sparkling-2-line text-2xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-base mb-1.5">
              No personalized picks yet
            </h3>
            <p className="text-dark-muted text-sm max-w-sm leading-relaxed">
              Upload a product image on Step 3 and we&apos;ll surface backgrounds
              tailored to it here. In the meantime, browse the aesthetic groups
              on the right.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {currentPageScenes.map((scene) => (
              <SceneCard
                key={scene.id}
                scene={scene}
                isActive={selected === scene.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3 pt-3">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed border-2 border-surface-dark bg-white text-dark hover:bg-primary hover:text-white hover:border-primary"
          >
            <i className="ri-arrow-left-line text-base" />
            Previous
          </button>
          <span className="text-sm font-medium text-dark px-2">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed border-2 border-surface-dark bg-white text-dark hover:bg-primary hover:text-white hover:border-primary"
          >
            Next
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      )}
      </>
      )}

      {/* Selected badge */}
      {selected && (
        <div className="flex-shrink-0 flex items-center justify-center gap-2 pt-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light border border-primary/20">
            <i className="ri-check-line text-primary text-xs" />
            <span className="text-xs font-semibold text-primary">
              {sceneNames[selected] || (isPatternTemplate(selected) ? productArrangements.find((p) => `${PATTERN_TEMPLATE_PREFIX}${p.id}` === selected)?.label + ' pattern' : selected)}
            </span>
            <span className="text-xs text-dark-muted">
              {isPatternTemplate(selected) ? 'Pattern' : currentGroup?.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}